import { AlertTriangle, Check, ChevronsUpDown, X } from "lucide-react";
import { useMemo, useState } from "react";

import { AdminConsentBadge, RiskBadge } from "@/components/app-registration-badges";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAppRegistrationScopes } from "@/lib/api";
import { groupScopes, indexScopes } from "@/lib/app-registration-scopes";
import { cn } from "@/lib/utils";

/**
 * Pick delegated permissions out of the server's catalogue.
 *
 * Lifted out of the intake dialog when amending an issued registration became
 * possible: a requester editing permissions is doing exactly what a requester
 * asking for them does, from the same catalogue, with the same risk and
 * admin-consent badges and the same cap. A second picker would have been the
 * one screen where `Mail.Read` could read differently.
 *
 * Presentation only -- it holds no request state and knows nothing about
 * amendments. The caller owns `selected`, which is what lets the amend form
 * seed it with what the registration already holds and diff against that.
 */
export function ScopePicker({
  selected,
  onToggle,
  disabled,
  atLimit,
  excluded,
}: {
  selected: string[];
  onToggle: (key: string) => void;
  disabled: boolean;
  atLimit: boolean;
  /** Catalogue keys not to offer at all, e.g. scopes the server always keeps. */
  excluded?: readonly string[];
}) {
  const [open, setOpen] = useState(false);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const { data: scopes, isLoading, error } = useAppRegistrationScopes();
  // Grouped for display only — the sections and the scopes inside them keep
  // the server's picker order, which runs least dangerous first. A group left
  // empty by `excluded` is dropped with it.
  const groups = useMemo(() => {
    const hidden = new Set(excluded ?? []);
    return groupScopes(scopes?.filter((scope) => !hidden.has(scope.key)));
  }, [scopes, excluded]);
  const byKey = useMemo(() => indexScopes(scopes), [scopes]);

  if (isLoading) {
    return (
      <div className="text-muted-foreground rounded-lg border p-3 text-sm">
        Loading the permission catalogue…
      </div>
    );
  }

  // A picker with no catalogue is not a picker with nothing to offer — it is a
  // picker that cannot be trusted, so say so instead of rendering an empty box
  // that reads as "no permissions are available".
  if (error || groups.length === 0) {
    return (
      <p className="text-destructive flex items-start gap-2 rounded-lg border p-3 text-sm">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        <span>
          Couldn&apos;t load the permission catalogue, so there is nothing to pick from. Close the
          dialog and reopen it to retry.
        </span>
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="app-reg-scopes"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className={selected.length === 0 ? "text-muted-foreground" : undefined}>
              {selected.length === 0
                ? "Choose delegated permissions…"
                : `${selected.length} permission${selected.length === 1 ? "" : "s"} selected`}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {/* Width is pinned to the trigger so the list can't overflow the
            dialog, and the scroll lives on the list rather than the popover
            so the search box stays put while the catalogue scrolls. */}
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search permissions…" />
            <CommandList className="max-h-72">
              <CommandEmpty>No permission matches that.</CommandEmpty>
              {groups.map(({ group, scopes: groupScopeList }) => (
                <CommandGroup key={group} heading={group}>
                  {groupScopeList.map((scope) => {
                    const isSelected = selectedSet.has(scope.key);
                    // At the cap, only already-selected rows stay actionable,
                    // so the limit reads as "unselect something" rather than a
                    // submit-time rejection.
                    const blocked = atLimit && !isSelected;
                    return (
                      <CommandItem
                        key={scope.key}
                        // Searched on the permission name and its description,
                        // not the catalogue key, which is what a requester
                        // reads and would type.
                        value={`${scope.value} ${scope.description}`}
                        disabled={blocked}
                        onSelect={() => onToggle(scope.key)}
                        className="items-start gap-3"
                      >
                        <Check
                          className={cn(
                            "mt-0.5 size-4 shrink-0",
                            isSelected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <div className="min-w-0 space-y-0.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-mono text-sm">{scope.value}</span>
                            <RiskBadge risk={scope.risk} />
                            {scope.admin_consent_required && <AdminConsentBadge />}
                          </div>
                          <p className="text-muted-foreground text-xs whitespace-normal">
                            {scope.description}
                          </p>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* The selection stays visible with the menu closed — a count alone
          would hide what is actually being asked for, which is the one thing
          the approver will judge. */}
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {selected.map((key) => (
            <li key={key}>
              <button
                type="button"
                onClick={() => onToggle(key)}
                disabled={disabled}
                aria-label={`Remove ${byKey.get(key)?.value ?? key}`}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/70 inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-xs disabled:opacity-50"
              >
                {byKey.get(key)?.value ?? key}
                <X className="size-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
